import React from 'react';
import TopNav from './TopNav';
import MobileHeader from './MobileHeader';
import SiteFooter from './SiteFooter';
import BottonNavigation from './BottonNavigation';

interface AppShellProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  /** Decorative background image URL, rendered behind content. */
  backdrop?: string;
  /** Opacity applied to the backdrop image. Defaults to 0.5. */
  backdropOpacity?: number;
  /**
   * Render the app's navigation chrome. Default true. Pre-auth screens
   * (login, register) pass false.
   */
  chrome?: boolean;
}

/**
 * Page frame shared by every screen. Two modes, switched purely in CSS.
 *
 * Below lg it is unchanged from the mobile design: a fixed-height
 * (`100dvh`) flex column where `<main>` is the only scrolling region and
 * the bottom nav is its flex sibling, so the nav pins structurally
 * rather than by coincidence.
 *
 * At lg and above, `lg:h-auto` and `lg:overflow-visible` release that
 * model so the document scrolls normally — the precondition for a sticky
 * TopNav and a real SiteFooter. Desktop rules are additive overrides;
 * no mobile class is modified.
 *
 * `paddingTop: env(safe-area-inset-top)` protects the header slot and
 * `<main>` from an iOS notch, now that `viewport-fit=cover` is set.
 *
 * Trade-off (mobile only): the fixed-height column expects header +
 * bottom nav to leave `<main>` positive space. On a very short viewport
 * with a tall header, `<main>` can clamp to zero. Accepted, to guarantee
 * the nav pins structurally.
 */
const AppShell: React.FC<AppShellProps> = ({
  children,
  header,
  backdrop,
  backdropOpacity = 0.5,
  chrome = true,
}) => (
  <div className="h-[100dvh] lg:h-auto lg:min-h-[100dvh] bg-brand-100 lg:bg-brand-50 flex lg:block justify-center">
    <div
      className="relative flex flex-col w-full max-w-app lg:max-w-none h-[100dvh] lg:h-auto lg:min-h-[100dvh] bg-brand-50"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {backdrop && (
        <div
          data-testid="backdrop"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{ backgroundImage: `url(${backdrop})`, opacity: backdropOpacity }}
        />
      )}
      {chrome && <TopNav />}
      {chrome && <MobileHeader />}
      {header && <div className="relative lg:hidden">{header}</div>}
      <main className="relative flex-1 overflow-y-auto pb-4 lg:overflow-visible lg:flex-none lg:pb-0">
        {children}
        {/* The mobile footer lives inside <main> because below lg that is the
            only scrolling region — as a flex sibling it would be a permanent
            band pinned above the bottom nav rather than something you scroll
            to. The desktop footer below stays a sibling, where `lg:mt-auto`
            can do its job. */}
        {chrome && <SiteFooter variant="mobile" />}
      </main>
      {chrome && <SiteFooter />}
      <BottonNavigation />
    </div>
  </div>
);

export default AppShell;
